import { Button } from "@/components/ui/button";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";
import { permissionDeniedLabel } from "@/constants/permission";
import { History, Pencil, Power, Trash2 } from "lucide-react";
import { RecipientActions } from "../hooks/use-recipients-source";
import { SECTOR_EMAILS_LABELS } from "../messages";
import { SectorRecipient } from "../types";
import { DeleteRecipientModal } from "./delete-recipient-modal";
import { RecipientFormModal } from "./recipient-form-modal";
import { RecipientHistoryModal } from "./recipient-history-modal";

interface RowActionsProps {
  recipient: SectorRecipient;
  actions: RecipientActions;
}

export function RowActions({
  recipient,
  actions,
}: RowActionsProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const { canUpdate, canDelete } = useAssignmentAbilities(
    AssignmentsEnum.SHIPPING_RECIPIENT,
  );

  const labelFor = (action: string, allowed: boolean): string =>
    allowed ? action : permissionDeniedLabel(action);

  const openHistory = (): void => {
    handleModal({
      isOpen: true,
      title: SECTOR_EMAILS_LABELS.HISTORY,
      element: <RecipientHistoryModal recipient={recipient} />,
    });
  };

  const openEdit = (): void => {
    handleModal({
      isOpen: true,
      title: SECTOR_EMAILS_LABELS.EDIT_RECIPIENT,
      element: (
        <RecipientFormModal
          recipient={recipient}
          onSubmit={(input) => actions.update(recipient.id, input)}
        />
      ),
    });
  };

  const openDelete = (): void => {
    handleModal({
      isOpen: true,
      title: SECTOR_EMAILS_LABELS.DELETE_RECIPIENT,
      element: (
        <DeleteRecipientModal
          recipient={recipient}
          onConfirm={actions.remove}
        />
      ),
    });
  };

  const toggleLabel: string = recipient.active
    ? SECTOR_EMAILS_LABELS.DEACTIVATE
    : SECTOR_EMAILS_LABELS.REACTIVATE;

  return (
    <div className="flex items-center gap-0.5">
      <Button
        variant="ghost_none"
        className="h-8 w-8 border-none bg-transparent p-0"
        title={SECTOR_EMAILS_LABELS.HISTORY}
        aria-label={SECTOR_EMAILS_LABELS.HISTORY}
        onClick={openHistory}
      >
        <History className="h-4 w-4 text-[#613F4F]" />
      </Button>
      <Button
        variant="ghost_none"
        className="h-8 w-8 border-none bg-transparent p-0"
        title={labelFor(toggleLabel, canUpdate)}
        aria-label={labelFor(toggleLabel, canUpdate)}
        disabled={!canUpdate}
        onClick={() => actions.toggleActive(recipient)}
      >
        <Power
          className={`h-4 w-4 ${
            recipient.active ? "text-gray-600" : "text-green-600"
          }`}
        />
      </Button>
      <Button
        variant="ghost_none"
        className="h-8 w-8 border-none bg-transparent p-0"
        title={labelFor(SECTOR_EMAILS_LABELS.EDIT, canUpdate)}
        aria-label={labelFor(SECTOR_EMAILS_LABELS.EDIT, canUpdate)}
        disabled={!canUpdate}
        onClick={openEdit}
      >
        <Pencil className="h-4 w-4 text-gray-600" />
      </Button>
      <Button
        variant="ghost_none"
        className="h-8 w-8 border-none bg-transparent p-0"
        title={labelFor(SECTOR_EMAILS_LABELS.DELETE, canDelete)}
        aria-label={labelFor(SECTOR_EMAILS_LABELS.DELETE, canDelete)}
        disabled={!canDelete}
        onClick={openDelete}
      >
        <Trash2 className="h-4 w-4 text-red-600" />
      </Button>
    </div>
  );
}
